import React, { useEffect, useState } from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { __IS_DEVELOPMENT__ } from "../../globals";
import { usePrimaryOrganizationId } from "../../hooks";
import { usePrivacyCenter } from "../../hooks/usePrivacyCenter";
import { formatDateWithTimeOnly } from "../../formatters/date";
import { Api } from "../../../common/service/Api";
import { useMutation } from "@tanstack/react-query";
import { CreateOrganizationJwt } from "../../../common/service/server/controller/OrganizationController";

const THEME_QUERY_PARAM_NAMES = [
  "activeColor",
  "inactiveColor",
  "backgroundColor",
  "mainFont",
  "mainFontSize",
  "bodyFont",
  "bodyFontSize",
  "navFont",
  "navFontSize",
  "sectionFont",
  "sectionFontSize",
];

export function PrivacyCenterPreviewPopupPage() {
  const orgId = usePrimaryOrganizationId();
  const [privacyCenter] = usePrivacyCenter(orgId);
  const uuid = self.crypto.randomUUID();

  const [lastReloadedAtTime, setLastReloadedAtTime] = useState(undefined);

  const [jwt, setJwt] = useState(null);
  const [generatingJwt, setGeneratingJwt] = useState(null);

  function useOrgJwt() {
    const { mutateAsync: createJwt, ...rest } = useMutation(
      ({ expiryInSeconds }: CreateOrganizationJwt) =>
        Api.organization.createOrgJwt(orgId, { expiryInSeconds: expiryInSeconds }),
      {
        onSuccess: (data) => {
          setJwt(data.token);
          setGeneratingJwt(false);
        },
      },
    );

    return { createJwt, ...rest };
  }

  const orgJwtResult = useOrgJwt();
  const { createJwt } = orgJwtResult;

  useEffect(() => {
    if (jwt || generatingJwt) return;

    createJwt({ expiryInSeconds: 45 * 60 /* 45 minutes */ });
    setGeneratingJwt(orgJwtResult.isLoading);
  }, [jwt, orgJwtResult]);

  useEffect(() => {
    if (lastReloadedAtTime !== undefined) return;
    setLastReloadedAtTime(new Date());
  }, []);

  const urlParams = new URLSearchParams(window.location.search);
  const previewParams = new URLSearchParams();
  for (const [key, value] of urlParams) {
    if (THEME_QUERY_PARAM_NAMES.includes(key)) {
      previewParams.append(key, value);
    }
  }

  let previewThemeQueryParams = previewParams.toString();

  // Get optional path parameter to navigate directly to a specific page (e.g., /privacy-request)
  const pathParam = urlParams.get("path") || "";

  return (
    jwt && (
      <div style={{ height: "100vh" }}>
        <div
          className={"bg-neutral-100 d-flex"}
          style={{
            paddingTop: "11px",
            paddingBottom: "11px",
            justifyContent: "center",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <VisibilityIcon />
          {lastReloadedAtTime && (
            <span>Last Updated: {formatDateWithTimeOnly(lastReloadedAtTime)}</span>
          )}
          <span>You are in Preview Mode and won't be able to submit Privacy Requests.</span>
        </div>
        <iframe
          src={
            __IS_DEVELOPMENT__
              ? `http://localhost:3000${pathParam}?_preview=true&id=${uuid}&${previewThemeQueryParams}&token=${jwt}`
              : `${privacyCenter?.url}${pathParam}?_preview=true&id=${uuid}&${previewThemeQueryParams}&token=${jwt}`
          }
          width={"100%"}
          height={"100%"}
        ></iframe>
      </div>
    )
  );
}
